import moment from "moment";
import css from "./RecentInvoices.css";
import React, { useEffect, useRef, useMemo, useCallback } from "react";
import { Column } from "primereact/column";
import "react-toastify/dist/ReactToastify.css";
import MemberName from "./elements/memberName";
import { DataTable } from "primereact/datatable";
import { ThreeDots } from "react-loader-spinner";
import { useSelector, useDispatch } from "react-redux";
import { dateConvert } from "../../helpers/dateHelper";
import { renderCTA } from "../../helpers/invoiceHelpers";
import { useFetchRecentInvoices } from "./DashboardCurrencyApi";
import { setHeaderText } from "../../features/headerText/headerTextSlice";
import PaymentStatusWise from "../Invoices/elements/paymentStatusWise.jsx";
import { motion } from "framer-motion";
import { setAppLoading } from "../../features/appSettings/appSettingsSlice.js";

const RecentInvoices = (props) => {
  const currencySum = props.sum;
  const dispatch = useDispatch();
  const dashboradDate = useSelector((state) => state.invoice.dashboardDateRange);
  const dashboradCurrency = useSelector((state) => state.invoice.dashboardCurrency);
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const { appSettings } = useSelector((state) => state.appSettings);

  // Memoize date range for stable reference in useQuery
  const dateRange = useMemo(() => {
    const start = moment(dashboradDate[0]).tz(global.config.getTimeZone()).startOf("day").toISOString();
    const end = moment(dashboradDate[1]).tz(global.config.getTimeZone()).endOf("day").toISOString();
    return { start, end };
  }, [dashboradDate]);

  const {
    data: products = [],
    isLoading,
    refetch: refetchRecentInvoice,
  } = useFetchRecentInvoices(
    token,
    dateRange.start,
    dateRange.end,
    100,
    dashboradCurrency,
    currencySum
  );

  // Memoize sliced products to prevent recalculation on every render
  const displayedProducts = useMemo(() => products.slice(0, 5), [products]);

  // Handle loading state with useCallback to avoid recreation
  const handleLoading = useCallback(async () => {
    try {
      await dispatch(setAppLoading(isLoading));
    } catch (error) {
      console.error("Error setting app loading state:", error);
      if (global?.config?.notifyError) {
        global.config.notifyError("Failed to update loading state.");
      }
    }
  }, [isLoading, dispatch]);

  useEffect(() => {
    handleLoading();
  }, [handleLoading]);

  // Handle refetch with useCallback for stable reference
  const refetchDashboardData = useCallback(async () => {
    try {
      await dispatch(setHeaderText("Dashboard"));
      await refetchRecentInvoice();
    } catch (error) {
      console.error("Error while refetching dashboard data:", error);
      if (global?.config?.notifyError) {
        global.config.notifyError("Failed to refresh dashboard data.");
      }
    }
  }, [dispatch, refetchRecentInvoice]);

  useEffect(() => {
    refetchDashboardData();
  }, [refetchDashboardData, dashboradDate, dashboradCurrency, currencySum, token, user]);

  // Memoize renderAmount to prevent recreation
  const renderAmount = useCallback((rowData) => {
    try {
      if (!rowData || typeof rowData.inv_total_amount !== "number" || !rowData.inv_currency) {
        console.warn("Invalid data for renderAmount:", rowData);
        return <span>--</span>;
      }
      const totalAmount = rowData.inv_total_amount.toLocaleString("en-US", {
        style: "currency",
        currency: rowData.inv_currency,
      });
      return <span>{totalAmount}</span>;
    } catch (error) {
      console.error("Error rendering amount:", error, rowData);
      return <span>--</span>;
    }
  }, []);

  const componentRef = useRef(null);
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  return (
    <div className="invoice-outer">
      <div className="recent-title">
        {global.config.locate(appSettings && appSettings.language, "Recent Invoices")}
      </div>
      <div className="card">
        {isLoading ? (
          <ThreeDots
            height="80"
            width="80"
            radius="9"
            color="#405BFF"
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName=""
            visible={true}
          />
        ) : (
          <motion.div
            ref={componentRef}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            transition={{ duration: 0.3 }}
          >
            <DataTable
              value={displayedProducts}
              removableSort
              tableStyle={{ minWidth: "50rem", width: "100%", textAlign: "left" }}
              paginator={false}
              paginatorClassName="recent-invoices-pagination"
              rows={5}
              emptyMessage="No invoices found"
            >
              <Column
                field="inv_number"
                header={global.config.locate(appSettings && appSettings.language, "Invoice ID")}
                sortable
                style={{ minWidth: "200px", paddingRight: "44px" }}
              />
              <Column
                field="inv_due_date"
                body={dateConvert}
                header={global.config.locate(appSettings && appSettings.language, "Due Date")}
                sortable
                style={{ minWidth: "230px", paddingRight: "44px" }}
              />
              {(user.userType !== 3 &&
                user.userType !== 5 &&
                user.userType !== 4 &&
                user.userType !== 6 &&
                user.userType !== 7) && (
                <Column
                  field="fullName"
                  header={global.config.locate(appSettings && appSettings.language, "User Name")}
                  body={(rowData) => <MemberName userId={rowData.inv_for} />}
                  sortField="fullName"
                  style={{ minWidth: "240px", paddingRight: "44px" }}
                />
              )}
              <Column
                field="payment_status"
                header={global.config.locate(appSettings && appSettings.language, "Payment Status")}
                body={(rowData) => (
                  <PaymentStatusWise
                    inv_payment_details={rowData.inv_payment_details}
                    invData={rowData}
                    invoiceId={rowData.inv_number}
                    createdAt={rowData.created_at}
                    paymentStatus={rowData.inv_payment_status}
                  />
                )}
                style={{ minWidth: "200px", paddingRight: "44px" }}
              />
              <Column
                field="inv_total_amount"
                header={global.config.locate(appSettings && appSettings.language, "Amount")}
                body={renderAmount}
                sortable
                style={{ padding: "20px" }}
                className="bold-td"
              />
              <Column
                field="_id"
                body={renderCTA}
                style={{}}
              />
            </DataTable>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RecentInvoices;